import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { Principal, PrincipalRole, Tenant, TenantDirectoryUser } from '../../app/types';
import PageSection from '../shared/PageSection';

const PASSWORD_POLICY_LABEL = 'Password must be at least 8 characters and include uppercase, lowercase, digit, and symbol.';
const PASSWORD_POLICY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

interface TenantUsersPageProps {
  readonly principal: Principal;
  readonly tenantId: string;
  readonly tenants: Tenant[];
  readonly selectedTenantId: string;
  readonly onSelectTenant: (tenantId: string) => void;
  readonly users: TenantDirectoryUser[];
  readonly onCreateUser: (payload: {
    tenantId: string;
    email: string;
    role: PrincipalRole;
    username?: string;
    permanentPassword: string;
    sendInvite?: boolean;
  }) => Promise<void>;
  readonly onUpdateUser: (
    username: string,
    payload: { tenantId: string; role?: PrincipalRole; enabled?: boolean }
  ) => Promise<void>;
  readonly onResetPassword: (username: string, tenantId: string) => Promise<void>;
  readonly onResendInvite: (username: string, tenantId: string) => Promise<void>;
}

export default function TenantUsersPage({
  principal,
  tenantId,
  tenants,
  selectedTenantId,
  onSelectTenant,
  users,
  onCreateUser,
  onUpdateUser,
  onResetPassword,
  onResendInvite,
}: TenantUsersPageProps) {
  const isPlatformAdmin = principal.role === 'platform-admin';
  const scopedTenantId = isPlatformAdmin ? selectedTenantId : tenantId;
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<PrincipalRole>('operator');
  const [permanentPassword, setPermanentPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const passwordValid = PASSWORD_POLICY_REGEX.test(permanentPassword);
  const passwordMatches = permanentPassword === confirmPassword;

  const roleOptions = useMemo(() => {
    if (principal.role === 'platform-admin') {
      return ['platform-admin', 'tenant-admin', 'operator', 'viewer'] as const;
    }
    return ['tenant-admin', 'operator', 'viewer'] as const;
  }, [principal.role]);

  const submit = async () => {
    if (!scopedTenantId) {
      setError('Select a tenant before creating a user.');
      return;
    }
    if (!permanentPassword || !confirmPassword) {
      setError('Password and confirm password are required.');
      return;
    }
    if (!passwordValid) {
      setError(PASSWORD_POLICY_LABEL);
      return;
    }
    if (permanentPassword !== confirmPassword) {
      setError('Password and confirm password must match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onCreateUser({
        tenantId: scopedTenantId,
        email,
        username: username || undefined,
        role,
        permanentPassword,
        sendInvite: false,
      });
      setEmail('');
      setUsername('');
      setRole(roleOptions[0]);
      setPermanentPassword('');
      setConfirmPassword('');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to create user.');
    } finally {
      setSaving(false);
    }
  };

  const safeRoleUpdate = async (targetUsername: string, nextRole: PrincipalRole) => {
    if (!scopedTenantId) {
      setError('Select a tenant before updating users.');
      return;
    }
    try {
      setError('');
      await onUpdateUser(targetUsername, { tenantId: scopedTenantId, role: nextRole });
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : 'Unable to update role.');
    }
  };

  const safeStatusToggle = async (target: TenantDirectoryUser) => {
    if (!scopedTenantId) {
      setError('Select a tenant before updating users.');
      return;
    }
    try {
      setError('');
      await onUpdateUser(target.username, { tenantId: scopedTenantId, enabled: !target.enabled });
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : 'Unable to update status.');
    }
  };

  const triggerReset = async (targetUsername: string) => {
    if (!scopedTenantId) {
      setError('Select a tenant before resetting passwords.');
      return;
    }
    try {
      setError('');
      await onResetPassword(targetUsername, scopedTenantId);
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : 'Unable to reset password.');
    }
  };

  const triggerResend = async (targetUsername: string) => {
    if (!scopedTenantId) {
      setError('Select a tenant before resending invites.');
      return;
    }
    try {
      setError('');
      await onResendInvite(targetUsername, scopedTenantId);
    } catch (resendError) {
      setError(resendError instanceof Error ? resendError.message : 'Unable to resend invite.');
    }
  };

  return (
    <Stack spacing={3}>
      <PageSection title="Tenant Users" subtitle="Cognito users scoped to the selected tenant.">
        {error ? <Alert severity="error">{error}</Alert> : null}
        {isPlatformAdmin ? (
          <TextField
            sx={{ mt: 2, maxWidth: 420 }}
            select
            label="Tenant"
            value={selectedTenantId}
            onChange={(event) => onSelectTenant(event.target.value)}
            fullWidth
            required
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                {tenant.displayName} ({tenant.tenantId})
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <TableContainer sx={{ mt: 2, width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 940 }}>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.username} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email ?? '-'}</TableCell>
                  <TableCell sx={{ minWidth: 170 }}>
                    <TextField
                      select
                      size="small"
                      value={user.role}
                      disabled={!scopedTenantId}
                      onChange={(event) => void safeRoleUpdate(user.username, event.target.value as PrincipalRole)}
                      fullWidth
                    >
                      {roleOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>{user.enabled ? 'Enabled' : 'Disabled'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button variant="outlined" size="small" onClick={() => void safeStatusToggle(user)}>
                        {user.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => void triggerReset(user.username)}
                        disabled={!scopedTenantId}
                      >
                        Reset Password
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => void triggerResend(user.username)}
                        disabled={!scopedTenantId}
                      >
                        Resend Invite
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">No users found for this tenant.</Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </PageSection>

      <PageSection title="Create Tenant User" subtitle="Creates a Cognito user with a permanent password.">
        <Stack spacing={2}>
          {isPlatformAdmin ? (
            <TextField
              select
              label="Tenant"
              value={selectedTenantId}
              onChange={(event) => onSelectTenant(event.target.value)}
              required
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.displayName} ({tenant.tenantId})
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <TextField
            label="Username (optional)"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(event) => setRole(event.target.value as PrincipalRole)}
          >
            {roleOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Password"
            type="password"
            value={permanentPassword}
            onChange={(event) => setPermanentPassword(event.target.value)}
            error={Boolean(permanentPassword) && !passwordValid}
            helperText={Boolean(permanentPassword) && !passwordValid ? PASSWORD_POLICY_LABEL : PASSWORD_POLICY_LABEL}
            required
          />
          <TextField
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            error={Boolean(confirmPassword) && !passwordMatches}
            helperText={confirmPassword && !passwordMatches ? 'Passwords must match.' : ' '}
            required
          />
          <Button
            variant="contained"
            onClick={() => void submit()}
            disabled={!email || !scopedTenantId || !permanentPassword || !confirmPassword || !passwordMatches || !passwordValid || saving}
          >
            {saving ? 'Creating...' : 'Create User'}
          </Button>
        </Stack>
      </PageSection>
    </Stack>
  );
}
